import type { FetchedWasteTypes } from "@/Utils/api/sanity/types";
import { getUniqKeys } from "@/Utils/getUniqKeys";
import { sortByOrderRank } from "@/Utils/wasteStreams";
import type { RecyclingRatePoint } from "@/components/v2/TrendView/recyclingRate/types";
import type {
  WasteAmountsPoint,
  WasteAmountsStream,
} from "@/components/v2/TrendView/wasteAmounts/types";
import type { WasteTypeConfig } from "@/pages/configuration/useConfigService";
import {
  COLORS,
  getNewColor,
} from "@/pages/customer/dashboard/functions/pieChartData";

import { uniq } from "lodash";
import moment from "moment";
import { FilterTimeFrameValue } from "server/config-api/index.js";
import type { QuantityUnit, WasteType } from "server/dwhApi/common.js";
import type {
  GetTerminalAggregatesPeriodsResponse,
  GetTerminalPeriodsResponse,
} from "server/dwhApi/terminals/models.js";
import type {
  Terminal,
  TerminalPeriod,
} from "server/dwhApi/terminals/types.js";

export const getDataWithCurrentMonth = ({
  periodData,
  period,
}: {
  periodData?: GetTerminalPeriodsResponse;
  period: FilterTimeFrameValue;
}) => {
  const data = [...(periodData?.periods || [])];

  const currentMonth = data[data.length - 1];
  if (currentMonth) {
    const { totalOccurrences } = currentMonth.terminal;

    if (totalOccurrences > 0) {
      if (period === FilterTimeFrameValue.Last12Months && data.length > 12) {
        data.shift();
      }
    } else {
      data.pop();
    }
  }

  return data;
};

export const getRecyclingRateGraphData = ({
  data,
  getPeriodLabelValue,
  comparisonYearBeforeData,
  portfolioComparisonData,
}: {
  data: TerminalPeriod[];
  getPeriodLabelValue: (startTime: string) => string;
  comparisonYearBeforeData?: GetTerminalPeriodsResponse;
  portfolioComparisonData?: GetTerminalAggregatesPeriodsResponse;
}): RecyclingRatePoint[] => {
  return data.map((d) => {
    const point: RecyclingRatePoint = {
      label: getPeriodLabelValue(d.startTime),
      rate: d.terminal.sortingDegree * 100,
    };

    if (comparisonYearBeforeData) {
      const comparisonPeriod = comparisonYearBeforeData.periods.find(
        (p) =>
          moment(p.startTime).format("MMM") ===
          moment(d.startTime).format("MMM"),
      );

      if (comparisonPeriod) {
        point.prevYearRate = comparisonPeriod.terminal.sortingDegree * 100;
      }
    }

    if (portfolioComparisonData) {
      const comparisonPeriod = portfolioComparisonData.periods.find(
        (p) =>
          moment(p.startTime).format("MMM YYYY") ===
          moment(d.startTime).format("MMM YYYY"),
      );

      if (comparisonPeriod) {
        point.portfolioAverage =
          comparisonPeriod.terminalAggregates.sortingDegree * 100;
      }
    }

    return point;
  });
};

type WasteAmountsProps = {
  wasteTypes?: FetchedWasteTypes | null;
  wasteTypeConfig?: WasteTypeConfig[];
  getPeriodLabelValue: (startTime: string) => string;
};

const getBarData = ({
  terminal,
  startTime,
  wasteTypes,
  wasteTypeConfig,
  getPeriodLabelValue,
}: WasteAmountsProps & { terminal: Terminal; startTime: string }) => {
  const parsedWasteTypes = terminal?.wasteTypes?.reduce(
    (
      prev: any,
      { code: wasteCode, weight = {} as QuantityUnit }: WasteType,
    ) => {
      const wasteType = wasteTypes?.find((w) => w.id === wasteCode);

      const customName = wasteTypeConfig?.find(
        (wtc) => wtc.wasteTypeCode === wasteCode,
      )?.name;
      const wasteTypeName = customName || wasteType?.name || wasteCode;
      return {
        ...prev,
        total: prev.total + weight.quantity,
        [wasteTypeName]: weight.quantity,
      };
    },
    { total: 0 },
  );
  return { label: getPeriodLabelValue(startTime), ...parsedWasteTypes };
};

export const getWasteAmountsGraphData = (
  props: WasteAmountsProps & { data: TerminalPeriod[] },
): WasteAmountsPoint[] =>
  props.data.map((d) =>
    getBarData({ ...props, terminal: d.terminal, startTime: d.startTime }),
  );

const getWasteCodes = ({
  data,
  wasteTypes,
}: {
  data: TerminalPeriod[];
  wasteTypes: FetchedWasteTypes | null;
}) => {
  if (!data) return [];

  const types = uniq(
    data?.flatMap(({ terminal }: { terminal: Terminal }) => {
      return terminal?.wasteTypes?.map(({ code }: WasteType) => code) ?? [];
    }),
  );

  return types.map((wasteCode: string) => ({
    id: wasteCode,
    name: wasteTypes?.find((w) => w.id === wasteCode)?.name,
  }));
};

export const getWasteStreams = ({
  data,
  wasteAmountData,
  wasteTypes,
  wasteTypeConfig,
}: {
  data: TerminalPeriod[];
  wasteAmountData: WasteAmountsPoint[];
  wasteTypes: FetchedWasteTypes | null;
  wasteTypeConfig?: WasteTypeConfig[];
}): WasteAmountsStream[] => {
  let curColors = [...COLORS];
  const wasteCodes = getWasteCodes({ data, wasteTypes });

  const streams = sortByOrderRank(
    getUniqKeys(wasteAmountData)
      .filter((item) => item !== "label" && item !== "total")
      .map((el) => {
        const [color, newColors] = getNewColor(curColors);
        curColors = newColors;

        const wasteCode = wasteCodes?.find((w) => w.name === el)?.id;
        const customColor = wasteTypeConfig?.find(
          (wtc) => wtc.name === el || wtc.wasteTypeCode === wasteCode,
        )?.color;

        const sanityWasteType = wasteTypes?.find((wt) => wt.id === wasteCode);
        const sanityDefaultColor = sanityWasteType?.wasteTypeDefaultColor;
        return {
          key: el,
          color: customColor || sanityDefaultColor || color,
          name: el,
          code: wasteCode,
        };
      }),
    wasteTypes || [],
    "code",
  );

  return streams;
};
